/**
 * CTRL_10 Dashboard - Change Management & Incident Response
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Progress, Timeline } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  RocketOutlined,
  RollbackOutlined,
  FileSearchOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

const UPDATE_TYPES = {
  'major': { label: 'Major', color: '#f5222d' },
  'minor': { label: 'Minor', color: '#fa8c16' },
  'patch': { label: 'Patch', color: '#52c41a' },
  'hotfix': { label: 'Hotfix', color: '#722ed1' }
};

function CTRL_10_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_10';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate deployment stats
  const deploymentStats = useMemo(() => {
    const deployments = events.filter(e => e.event_type === 'model_deployment' || e.event_type === 'model_update');
    const rollbacks = events.filter(e => e.event_metadata?.rollback === true);

    return {
      total: deployments.length,
      rollbacks: rollbacks.length,
      rollbackRate: deployments.length > 0 ? Math.round((rollbacks.length / deployments.length) * 100) : 0
    };
  }, [events]);

  // Calculate impact assessment coverage
  const impactAssessmentCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, completed: 0, percentage: 0 };

    const completed = events.filter(e =>
      e.event_metadata?.impact_assessment_completed === true
    ).length;

    return {
      total: events.length,
      completed,
      percentage: Math.round((completed / events.length) * 100)
    };
  }, [events]);

  // Calculate testing completion rate
  const testingCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, completed: 0, percentage: 0 };

    const completed = events.filter(e =>
      e.event_metadata?.testing_completed === true
    ).length;

    return {
      total: events.length,
      completed,
      percentage: Math.round((completed / events.length) * 100)
    };
  }, [events]);

  // Calculate rollback plan coverage
  const rollbackPlanCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, exists: 0, percentage: 0 };

    const exists = events.filter(e =>
      e.event_metadata?.rollback_plan_exists === true
    ).length;

    return {
      total: events.length,
      exists,
      percentage: Math.round((exists / events.length) * 100)
    };
  }, [events]);

  // Calculate approval rate
  const approvalRate = useMemo(() => {
    if (events.length === 0) return { total: 0, approved: 0, percentage: 0 };

    const approved = events.filter(e =>
      e.event_metadata?.approval_obtained === true
    ).length;

    return {
      total: events.length,
      approved,
      percentage: Math.round((approved / events.length) * 100)
    };
  }, [events]);

  // Recent deployments for timeline
  const recentDeployments = useMemo(() => {
    return events
      .slice(0, 10)
      .map(e => ({
        timestamp: new Date(e.ts),
        version: e.event_metadata?.version || 'Unknown',
        updateType: e.event_metadata?.update_type || 'unknown',
        rollback: e.event_metadata?.rollback === true
      }));
  }, [events]);

  // Table columns
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (ts) => new Date(ts).toLocaleString(),
      width: 180
    },
    {
      title: 'Version',
      dataIndex: ['event_metadata', 'version'],
      key: 'version',
      render: (version) => version ? <Tag color="blue">{version}</Tag> : '-'
    },
    {
      title: 'Update Type',
      key: 'update_type',
      render: (_, record) => {
        const type = record.event_metadata?.update_type || 'unknown';
        const typeInfo = UPDATE_TYPES[type] || { label: type, color: '#888' };
        return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>;
      }
    },
    {
      title: 'Impact Assessment',
      key: 'impact',
      align: 'center',
      render: (_, record) => {
        const completed = record.event_metadata?.impact_assessment_completed === true;
        return completed ?
          <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag> :
          <Tag color="red" icon={<WarningOutlined />}>No</Tag>;
      }
    },
    {
      title: 'Rollback Plan',
      key: 'rollback_plan',
      align: 'center',
      render: (_, record) => {
        const exists = record.event_metadata?.rollback_plan_exists === true;
        return exists ?
          <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag> :
          <Tag color="red" icon={<WarningOutlined />}>No</Tag>;
      }
    },
    {
      title: 'Testing',
      key: 'testing',
      align: 'center',
      render: (_, record) => {
        const completed = record.event_metadata?.testing_completed === true;
        return completed ?
          <Tag color="green">Complete</Tag> :
          <Tag color="orange">Pending</Tag>;
      }
    },
    {
      title: 'Approval',
      key: 'approval',
      align: 'center',
      render: (_, record) => {
        const approved = record.event_metadata?.approval_obtained === true;
        return approved ?
          <Tag color="green" icon={<SafetyOutlined />}>Approved</Tag> :
          <Tag color="red">Pending</Tag>;
      }
    },
    {
      title: 'Rollback',
      key: 'rollback',
      align: 'center',
      render: (_, record) => {
        const isRollback = record.event_metadata?.rollback === true;
        return isRollback ?
          <Tag color="red" icon={<RollbackOutlined />}>Yes</Tag> :
          <Tag color="green">No</Tag>;
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <Empty
        description="Please select a project to view dashboard"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_10: Change Management & Incident Response Dashboard</h2>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Deployments"
              value={deploymentStats.total}
              prefix={<RocketOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Rollback Rate"
              value={deploymentStats.rollbackRate}
              suffix="%"
              prefix={<RollbackOutlined />}
              valueStyle={{ color: deploymentStats.rollbackRate < 10 ? '#52c41a' : '#f5222d' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {deploymentStats.rollbacks} rollbacks
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Impact Assessment"
              value={impactAssessmentCoverage.percentage}
              suffix="%"
              prefix={<FileSearchOutlined />}
              valueStyle={{ color: impactAssessmentCoverage.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Approval Rate"
              value={approvalRate.percentage}
              suffix="%"
              prefix={<SafetyOutlined />}
              valueStyle={{ color: approvalRate.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Change Management Compliance */}
      <Card title="Change Management Compliance" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Impact Assessment"
                value={impactAssessmentCoverage.completed}
                suffix={`/ ${impactAssessmentCoverage.total}`}
                valueStyle={{ color: '#1890ff' }}
              />
              <Progress
                percent={impactAssessmentCoverage.percentage}
                strokeColor="#1890ff"
                size="small"
                style={{ marginTop: '8px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Rollback Plan"
                value={rollbackPlanCoverage.exists}
                suffix={`/ ${rollbackPlanCoverage.total}`}
                valueStyle={{ color: '#52c41a' }}
              />
              <Progress
                percent={rollbackPlanCoverage.percentage}
                strokeColor="#52c41a"
                size="small"
                style={{ marginTop: '8px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Testing Complete"
                value={testingCoverage.completed}
                suffix={`/ ${testingCoverage.total}`}
                valueStyle={{ color: '#722ed1' }}
              />
              <Progress
                percent={testingCoverage.percentage}
                strokeColor="#722ed1"
                size="small"
                style={{ marginTop: '8px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Approvals"
                value={approvalRate.approved}
                suffix={`/ ${approvalRate.total}`}
                valueStyle={{ color: '#fa8c16' }}
              />
              <Progress
                percent={approvalRate.percentage}
                strokeColor="#fa8c16"
                size="small"
                style={{ marginTop: '8px' }}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Deployment Timeline */}
      {recentDeployments.length > 0 && (
        <Card title="Recent Deployment History" style={{ marginBottom: '24px' }}>
          <Timeline>
            {recentDeployments.map((deployment, idx) => (
              <Timeline.Item
                key={idx}
                color={deployment.rollback ? 'red' : 'green'}
                dot={deployment.rollback ? <RollbackOutlined /> : <RocketOutlined />}
              >
                <div>
                  <strong>{deployment.version}</strong>
                  <Tag color={UPDATE_TYPES[deployment.updateType]?.color || '#888'} style={{ marginLeft: '8px' }}>
                    {UPDATE_TYPES[deployment.updateType]?.label || deployment.updateType}
                  </Tag>
                  {deployment.rollback && <Tag color="red">Rollback</Tag>}
                </div>
                <div style={{ color: '#666', fontSize: '13px' }}>
                  {deployment.timestamp.toLocaleString()}
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>
      )}

      {/* Change Tracking Table */}
      <Card title="Change Tracking">
        <Table
          dataSource={events.slice(0, 20)}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} changes`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_10_Dashboard;
